/**
 * 掲示板機能のAPI呼び出し
 *
 * V3 API仕様に対応した掲示板の作成・編集・削除・取得機能
 */

import { apiV3 } from '@/utils/api';
import type { BulletinBoard, BulletinBoardFormData } from '../types';

// API Base URL
const BASE_URL = '/bulletin-board/posts';

// ===================================
// 型定義
// ===================================

export interface BulletinBoardResponse {
  success: boolean;
  data: BulletinBoard;
  message?: string;
}

export interface BulletinBoardListResponse {
  success: boolean;
  data: BulletinBoard[];
  meta: {
    total: number;
    count: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
    has_more_pages: boolean;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
    self: string;
  };
}

// Comments API用の型
export interface BulletinComment {
  id: number;
  bulletin_id: number;
  staff_id: number;
  content: string;
  staff?: {
    id: number;
    name: string;
    role: string;
  };
  created_at: string;
  updated_at: string;
}

export interface BulletinCommentResponse {
  success: boolean;
  data: BulletinComment;
  message?: string;
}

export interface BulletinCommentListResponse {
  success: boolean;
  data: BulletinComment[];
  meta: {
    total: number;
    count: number;
  };
}

// Statistics API用の型
export interface BulletinStatistics {
  total_posts: number;
  monthly_posts: number;
  pinned_posts: number;
  unread_bulletins: number;
  urgent_bulletins: number;
  categories: Record<string, number>;
}

export interface BulletinStatisticsResponse {
  success: boolean;
  data: BulletinStatistics;
}

// Options API用の型
export interface BulletinOption {
  value: string;
  label: string;
  color?: string;
}

export interface BulletinOptionsResponse {
  success: boolean;
  data: BulletinOption[];
}

// ===================================
// 掲示板API関数
// ===================================

/**
 * 掲示板を作成する
 */
export const createBulletinBoard = async (data: BulletinBoardFormData): Promise<BulletinBoard> => {
  const formData = new FormData();

  // 基本データを追加
  const { files, remove_files, ...basicData } = data;

  Object.entries(basicData).forEach(([key, value]) => {
    // boolean値フィールドの特別処理（undefined/nullでも処理する）
    if (key === 'is_pinned' || key === 'is_active') {
      // boolean値を確実に数値文字列に変換（undefinedやnullの場合はfalseとして扱う）
      const boolValue = Boolean(value);
      formData.append(key, boolValue ? '1' : '0');
    } else if (value !== undefined && value !== null) {
      if (key === 'store_id' && value === null) {
        // store_idがnullの場合は空文字として送信（全店舗対象）
        formData.append(key, '');
      } else if (key === 'publish_start' || key === 'publish_end') {
        // 日付フィールドの形式を修正（T → スペース、秒を追加）
        const dateStr = String(value);
        if (dateStr.includes('T')) {
          // YYYY-MM-DDTHH:mm → YYYY-MM-DD HH:mm:ss
          formData.append(key, dateStr.replace('T', ' ') + ':00');
        } else {
          formData.append(key, dateStr);
        }
      } else {
        formData.append(key, String(value));
      }
    }
  });

  // ファイルを追加
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files[]', file);
    });
  }

  // 削除対象ファイル一覧を追加
  if (remove_files && remove_files.length > 0) {
    remove_files.forEach((filePath) => {
      formData.append('remove_files[]', filePath);
    });
  }

  const response = await apiV3.post<BulletinBoardResponse>(BASE_URL, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.data;
};

/**
 * 掲示板を更新する
 */
export const updateBulletinBoard = async (
  id: number,
  data: Partial<BulletinBoardFormData>
): Promise<BulletinBoard> => {
  const formData = new FormData();

  // PUTメソッド用のmethod overriding
  formData.append('_method', 'PUT');

  // 基本データを追加
  const { files, remove_files, ...basicData } = data;

  Object.entries(basicData).forEach(([key, value]) => {
    // boolean値フィールドの特別処理（undefined/nullでも処理する）
    if (key === 'is_pinned' || key === 'is_active') {
      // boolean値を確実に数値文字列に変換（undefinedやnullの場合はfalseとして扱う）
      const boolValue = Boolean(value);
      formData.append(key, boolValue ? '1' : '0');
    } else if (value !== undefined && value !== null) {
      if (key === 'store_id' && value === null) {
        formData.append(key, '');
      } else if (key === 'publish_start' || key === 'publish_end') {
        const dateStr = String(value);
        if (dateStr.includes('T')) {
          formData.append(key, dateStr.replace('T', ' ') + ':00');
        } else {
          formData.append(key, dateStr);
        }
      } else {
        formData.append(key, String(value));
      }
    }
  });

  // ファイルを追加
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files[]', file);
    });
  }

  // 削除対象ファイル一覧を追加
  if (remove_files && remove_files.length > 0) {
    remove_files.forEach((filePath) => {
      formData.append('remove_files[]', filePath);
    });
  }

  const response = await apiV3.post<BulletinBoardResponse>(`${BASE_URL}/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.data;
};

/**
 * 掲示板を取得する
 */
export const getBulletinBoard = async (id: number): Promise<BulletinBoard> => {
  const response = await apiV3.get<BulletinBoardResponse>(`${BASE_URL}/${id}`);
  return response.data.data;
};

/**
 * 掲示板一覧を取得する
 */
export const getBulletinBoards = async (params?: {
  page?: number;
  per_page?: number;
  search?: string;
  category?: string;
  priority?: string;
  is_pinned?: boolean;
  store_id?: number;
}): Promise<BulletinBoardListResponse> => {
  const response = await apiV3.get<BulletinBoardListResponse>(BASE_URL, { params });
  return response.data;
};

/**
 * 掲示板を削除する
 */
export const deleteBulletinBoard = async (id: number): Promise<void> => {
  await apiV3.delete(`${BASE_URL}/${id}`);
};

/**
 * 掲示板の閲覧記録を作成する
 */
export const markAsRead = async (
  id: number
): Promise<{ success: boolean; message: string; unread_count?: number }> => {
  const response = await apiV3.post(`${BASE_URL}/${id}/mark-as-read`);
  return response.data;
};

// ===================================
// Comments API
// ===================================

/**
 * 掲示板のコメント一覧を取得
 */
export const getBulletinComments = async (
  bulletinId: number,
  params?: any
): Promise<BulletinCommentListResponse> => {
  const response = await apiV3.get<any>(`${BASE_URL}/${bulletinId}/comments`, { params });

  // V3 APIレスポンスをトランスフォーム（author → staff）
  const transformedData = response.data.data.map((comment: any) => ({
    id: comment.id,
    bulletin_id: comment.post_id,
    staff_id: comment.author_id,
    content: comment.body,
    staff: comment.author, // author → staff に変換
    created_at: comment.meta?.created_at || comment.created_at,
    updated_at: comment.meta?.updated_at || comment.updated_at,
  }));

  return {
    ...response.data,
    data: transformedData,
  };
};

/**
 * 掲示板にコメントを作成
 */
export const createBulletinComment = async (
  bulletinId: number,
  data: { content: string }
): Promise<BulletinComment> => {
  const response = await apiV3.post<any>(`${BASE_URL}/${bulletinId}/comments`, {
    content: data.content,
  });

  // V3 APIレスポンスをトランスフォーム（author → staff）
  const comment = response.data.data;
  return {
    id: comment.id,
    bulletin_id: comment.post_id,
    staff_id: comment.author_id,
    content: comment.body,
    staff: comment.author, // author → staff に変換
    created_at: comment.meta?.created_at || comment.created_at,
    updated_at: comment.meta?.updated_at || comment.updated_at,
  };
};

/**
 * 掲示板のコメントを削除
 */
export const deleteBulletinComment = async (
  bulletinId: number,
  commentId: number
): Promise<void> => {
  await apiV3.delete(`${BASE_URL}/${bulletinId}/comments/${commentId}`);
};

// ===================================
// Statistics API
// ===================================

/**
 * 掲示板の統計情報を取得
 */
export const getBulletinStatistics = async (): Promise<BulletinStatistics> => {
  const response = await apiV3.get<BulletinStatisticsResponse>('/bulletin-board/statistics');
  return response.data.data;
};

// ===================================
// Options API
// ===================================

/**
 * 掲示板カテゴリオプション一覧を取得
 */
export const getBulletinCategories = async (): Promise<BulletinOption[]> => {
  const response = await apiV3.get<BulletinOptionsResponse>('/bulletin-board/categories');
  return response.data.data;
};

/**
 * 掲示板優先度オプション一覧を取得
 */
export const getBulletinPriorities = async (): Promise<BulletinOption[]> => {
  const response = await apiV3.get<BulletinOptionsResponse>('/bulletin-board/priorities');
  return response.data.data;
};

// ===================================
// File API
// ===================================

/**
 * 掲示板ファイルのURLを生成
 */
export const getBulletinFileUrl = (filePath: string): string => {
  if (!filePath) return '';

  // 既に完全URLの場合はそのまま返す
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // 相対パスの場合はベースURLを追加
  return `/storage/${filePath.replace(/^\/+/, '')}`;
};
